import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Step, Workflow } from './Workflow';
import useFeature from 'hooks/useFeature';
import { selectPrequalOffer } from 'state/prequalOffer';
import { selectDriversLicense } from 'state/driversLicense';
import { FeatureGroups } from 'state/tenant';
import useResponsiveQuery from 'hooks/useResponsiveQuery';
import useQueryParameters from 'hooks/useQueryParameters';
import { keys, not, reject } from 'ramda';

const usePrequalDriverLicenseWorkflow = () => {
  const { isGroupEnabled } = useFeature();
  const { selectedCardKey } = useQueryParameters();
  const { isEnabled } = useFeature();
  const { prequalificationId, hasOffer } = useSelector(selectPrequalOffer);
  const { result: driversLicense } = useSelector(selectDriversLicense);
  const { isMobile } = useResponsiveQuery();
  const getWorkflow = useCallback((): Workflow => {
    const hasConsumerSearch = driversLicense === null;
    const stepsEnabled = {
      [Step.CARD_SELECTOR]: isEnabled('cardSelector') && !selectedCardKey,
      [Step.DRIVER_LICENSE_START]: true,
      [Step.SCAN_PAGE]: true,
      [Step.CONSUMER_SEARCH]: hasConsumerSearch,
      [Step.PREQUAL_OFFER_APPLICATION]: true,
      [Step.APPLICATION]: true,
      // Credit application has a second step, whereas prequalification
      // does not.
      [Step.APPLICATION_2]: !prequalificationId && !hasOffer && isMobile,
      [Step.ADD_ONS]: isGroupEnabled(FeatureGroups['add-ons']),
      [Step.REVIEW_TERMS]: true,
    };
    const firstStep = ((keys(
      reject(not, stepsEnabled),
    ) as unknown) as Step[])[0];
    return {
      stepsEnabled: stepsEnabled,
      paths: {
        [Step.CARD_SELECTOR]: '/card-selector',
        [Step.DRIVER_LICENSE_START]: '/prequal/driver-license/start',
        [Step.SCAN_PAGE]: '/driver-license/scan-page',
        [Step.CONSUMER_SEARCH]: '/prequal/consumer-search',
        [Step.PREQUAL_OFFER_APPLICATION]: '/prequal/offer-application',
        [Step.APPLICATION]: '/prequal/application',
        [Step.ADD_ONS]: '/add-ons',
        [Step.REVIEW_TERMS]: '/prequal/review-terms',
        ...{
          [firstStep]: '/prequal/driver-license',
        },
      },
      progressStartStep: Step.PREQUAL_OFFER_APPLICATION,
      restartStep: firstStep,
      timeoutRestartPath: '/prequal/driver-license',
    };
  }, [
    driversLicense,
    isEnabled,
    selectedCardKey,
    prequalificationId,
    hasOffer,
    isMobile,
    isGroupEnabled,
  ]);

  return getWorkflow;
};

export default usePrequalDriverLicenseWorkflow;
